/** @format */

import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
	ArrowLeft,
	ArrowRight,
	RotateCcw,
	Sun,
	Moon,
	Eye,
	EyeOff,
	Layers,
	Home,
	Bed,
	Thermometer,
	Sparkles,
	Zap,
	Check,
	SwatchBook,
	HelpCircle,
	Smartphone,
	User,
} from "lucide-react"; // Import relevant icons
import Stepper from "./Stepper";
import ContactBlock from "./contact/ContactBlock";

// --- Stepper Component (Assuming it exists and accepts steps, currentStep) ---

// --- Quiz Data Structure ---
const quizQuestions = [
	{
		id: "q1",
		question: "What's your main goal for this window?",
		options: [
			{
				value: "max_light_block",
				text: "Block Maximum Light",
				description: "For bedrooms, media rooms (total darkness).",
				icon: Moon,
			},
			{
				value: "balance_light_privacy",
				text: "Balance Light & Privacy",
				description: "Filter light gently, maintain privacy.",
				icon: Sun,
			}, // Could use Half Sun/Moon icon if available
			{
				value: "preserve_view",
				text: "Preserve the View",
				description: "See outside while reducing glare/heat.",
				icon: Eye,
			},
			{
				value: "insulation",
				text: "Improve Insulation",
				description: "Better temperature control, energy savings.",
				icon: Thermometer,
			}, // Or Snowflake/Sun
		],
	},
	{
		id: "q2",
		question: "Which room is this shade for?",
		options: [
			{ value: "bedroom", text: "Bedroom / Nursery", icon: Bed },
			{ value: "living", text: "Living / Dining Room", icon: Home }, // Could use TV or Sofa icon
			{ value: "office", text: "Office / Study", icon: Layers }, // Or Briefcase icon
			{
				value: "kitchen_bath",
				text: "Kitchen / Bathroom",
				description: "(Consider moisture resistance)",
				icon: Home,
			}, // Could use Droplet or Fork/Knife icon
			{ value: "other", text: "Other Room", icon: Home },
		],
	},
	{
		id: "q3",
		question:
			"How important is daytime privacy (preventing viewing IN from outside)?",
		// Simplified based on initial goal, could be more nuanced
		condition: (answers) => answers.q1 !== "max_light_block", // Only ask if not already choosing max blackout
		options: [
			{
				value: "high_privacy",
				text: "Very Important",
				description: "No visibility inwards during the day.",
				icon: EyeOff,
			},
			{
				value: "moderate_privacy",
				text: "Somewhat Important",
				description: "Silhouettes might be slightly visible.",
				icon: EyeOff,
			}, // Use slightly different icon maybe?
			{
				value: "low_privacy",
				text: "Not Important",
				description: "Maximizing light/view is key.",
				icon: Eye,
			},
		],
	},
	{
		id: "q4",
		question: "Which style best describes your preference?",
		options: [
			{
				value: "modern_sleek",
				text: "Modern & Sleek",
				description: "Clean lines, minimalist look.",
				icon: Layers,
			},
			{
				value: "soft_textured",
				text: "Soft & Textured",
				description: "Adds warmth, visual interest.",
				icon: Sparkles,
			}, // Or a Fabric icon
			{
				value: "unique_statement",
				text: "Unique & Statement",
				description: "Distinctive patterns or operation.",
				icon: Zap,
			}, // Or Star icon
		],
	},
];

// --- Recommendation Logic ---
// This is a simplified example, needs refinement based on your product specifics
const getRecommendations = (answers) => {
	const recs = [];
	const reasons = {}; // Store reasons for recommendations

	// Helper to add recommendation with reason
	const addRec = (type, reasonKey, reasonText, priority = 3) => {
		const existing = recs.find((r) => r.type === type);
		if (!existing) {
			recs.push({ type, priority }); // Lower number = higher priority
			reasons[type] = new Set();
		}
		// Add reason only if the type already exists (to avoid duplicate reasons array)
		if (reasons[type] && reasonKey) {
			reasons[type].add(reasonText);
		} else if (reasonKey) {
			reasons[type] = new Set([reasonText]); // Initialize if adding first reason
		}
	};

	// Q1: Main Goal
	if (answers.q1 === "max_light_block") {
		addRec("Blackout Roller", "q1", "Provides complete darkness", 1);
		addRec("Blackout Cellular", "q1", "Provides complete darkness", 1); // Cellular often has better edge sealing
	}
	if (answers.q1 === "balance_light_privacy") {
		addRec("Privacy Roller", "q1", "Balances light and privacy", 1);
		addRec(
			"Light Filtering Cellular",
			"q1",
			"Softly filters light with privacy",
			1
		);
		addRec("Zebra", "q1", "Offers versatile light/privacy control", 1);
		addRec("Light Filtering Sheer", "q1", "Softens light with some privacy", 2);
	}
	if (answers.q1 === "preserve_view") {
		addRec("Solar Roller", "q1", "Reduces glare/heat while keeping view", 1);
		addRec("Sheer", "q1", "Maximizes view and natural light", 1);
		addRec("Zebra", "q1", "Can align for view-through", 2);
	}
	if (answers.q1 === "insulation") {
		addRec("Cellular", "q1", "Excellent insulation properties", 1); // All cellular types
		addRec("Blackout Cellular", "q1", "Best insulation + darkness", 1);
		addRec("Light Filtering Cellular", "q1", "Good insulation + soft light", 1);
	}

	// Q2: Room Type (Refine based on Q1)
	if (answers.q2 === "bedroom") {
		// Boost priority of Blackout options
		recs.forEach((r) => {
			if (r.type.includes("Blackout")) r.priority = 0;
		});
		if (!recs.some((r) => r.type.includes("Blackout"))) {
			// Add if not already there
			addRec("Blackout Roller", "q2", "Ideal for bedrooms");
			addRec("Blackout Cellular", "q2", "Ideal for bedrooms");
		}
	}
	if (answers.q2 === "kitchen_bath") {
		// Note: Add moisture resistance reason if applicable to specific types
		addRec("Solar Roller", "q2", "Durable for kitchens/baths", 2); // Often PVC based
		addRec(
			"Faux Wood Blinds",
			"q2",
			"Moisture resistant (If you offered them)",
			2
		); // Example if you had blinds
	}
	// Add other room logic...

	// Q3: Daytime Privacy (Refine based on Q1)
	if (answers.q3 === "high_privacy" && answers.q1 !== "max_light_block") {
		recs.forEach((r) => {
			if (r.type.includes("Privacy") || r.type.includes("Blackout"))
				r.priority -= 1;
		}); // Boost privacy/blackout
		if (
			!recs.some(
				(r) => r.type.includes("Privacy") || r.type.includes("Blackout")
			)
		) {
			addRec("Privacy Roller", "q3", "Ensures daytime privacy");
		}
	}
	if (answers.q3 === "low_privacy" && answers.q1 !== "max_light_block") {
		recs.forEach((r) => {
			if (r.type.includes("Sheer") || r.type.includes("Solar")) r.priority -= 1;
		}); // Boost sheer/solar
		if (
			!recs.some((r) => r.type.includes("Sheer") || r.type.includes("Solar"))
		) {
			addRec("Solar Roller", "q3", "Prioritizes view over privacy");
			addRec("Sheer", "q3", "Maximizes view");
		}
	}

	// Q4: Style
	if (answers.q4 === "modern_sleek") {
		recs.forEach((r) => {
			if (r.type.includes("Roller") || r.type.includes("Zebra"))
				r.priority -= 1;
		});
		addRec("Roller", "q4", "Offers a clean, modern look", 2);
		addRec("Zebra", "q4", "Provides a sleek, contemporary style", 2);
	}
	if (answers.q4 === "soft_textured") {
		recs.forEach((r) => {
			if (r.type.includes("Cellular") || r.type.includes("Sheer"))
				r.priority -= 1;
		});
		addRec("Cellular", "q4", "Soft fabric adds warmth", 2);
		addRec("Sheer", "q4", "Elegant and airy feel", 2);
		addRec("Textured Roller", "q4", "Many rollers offer texture", 3); // Lower priority mention
	}
	if (answers.q4 === "unique_statement") {
		addRec("Zebra", "q4", "Unique striped appearance", 1);
		addRec("Patterned Roller", "q4", "Rollers offer statement patterns", 2);
	}

	// Sort by priority (lower number first) and take top 3
	const sortedRecs = recs.sort((a, b) => a.priority - b.priority).slice(0, 3);

	// Map to final structure with details (add images, links etc.)
	const finalRecommendations = sortedRecs.map((rec) => {
		// Find matching product details (replace with your actual product data lookup)
		const details = productDetailsMap[rec.type] || {
			name: rec.type,
			link: `/shop`,
			image: "/images/placeholder.jpg",
			shortDesc: "A great choice.",
		};
		return {
			...details,
			reasons: Array.from(reasons[rec.type] || []), // Convert Set to Array
		};
	});

	return finalRecommendations;
};

// --- Placeholder Product Details Map ---
// Replace with actual data or fetch dynamically
// --- Product Details Map (Completed) ---
// Maps recommendation keys to display details & links
const productDetailsMap = {
	// --- Roller Shades ---
	"Blackout Roller": {
		name: "Blackout Roller Shades",
		link: "/shop?type=Roller&lightControl=Blackout", // Filter by type & light control
		image: "/images/newHome/shades/blackout_roller.jpg",
		shortDesc: "100% light blockage for complete darkness and privacy.",
	},
	"Privacy Roller": {
		name: "Privacy Roller Shades",
		link: "/shop?type=Roller&lightControl=Privacy", // Filter by type & light control
		image: "/images/newHome/shades/privacy_roller2.jpg",
		shortDesc: "Significantly darkens the room while ensuring full privacy.",
	},
	"Solar Roller": {
		name: "Solar Roller Shades",
		link: "/shop?type=Roller&lightControl=Solar", // Filter by type & light control
		image: "/images/newHome/shades/privacy_roller.jpg", // You provided this, double-check if it shows solar effect
		shortDesc: "Reduces heat & glare while preserving your outside view.",
	},
	"Textured Roller": {
		// Added based on Q4 logic
		name: "Textured Roller Shades",
		link: "/shop?type=Roller", // Link to general Roller, user needs to find textures
		image: "/images/newHome/shades/privacy_roller2.jpg", // Placeholder - NEEDS texture image
		shortDesc:
			"Adds depth and visual interest with unique woven or natural textures.",
	},
	"Patterned Roller": {
		// Added based on Q4 logic
		name: "Patterned Roller Shades",
		link: "/shop?type=Roller", // Link to general Roller, user needs to find patterns
		image: "/images/newHome/shades/blackout_roller.jpg", // Placeholder - NEEDS pattern image
		shortDesc:
			"Make a statement with stylish patterns, from subtle to bold designs.",
	},
	Roller: {
		// Generic Roller (if recommended for style)
		name: "Roller Shades",
		link: "/shop?type=Roller", // General Roller link
		image: "/images/newHome/shades/privacy_roller2.jpg", // Use a common roller image
		shortDesc: "Sleek, modern, and available in many fabrics and styles.",
	},

	// --- Cellular Shades ---
	Cellular: {
		// Generic Cellular (if recommended for insulation/style)
		name: "Cellular Shades",
		link: "/shop?type=Simply+Cellular", // Main Cellular link (Note: space encoded as +)
		image: "/images/newHome/shades/day_night_cellular.jpg",
		shortDesc: "Offer excellent insulation, light control, and a soft look.",
	},
	"Blackout Cellular": {
		name: "Blackout Cellular Shades",
		link: "/shop?type=Simply+Cellular&lightControl=Blackout", // Filtered link
		image: "/images/newHome/shades/blackout_cellular.jpg",
		shortDesc: "Maximum insulation combined with complete light blockage.",
	},
	"Light Filtering Cellular": {
		name: "Light Filtering Cellular Shades",
		link: "/shop?type=Simply+Cellular&lightControl=Soft+Filter", // Filtered link (Note: space encoded as +)
		image: "/images/newHome/shades/light_cellular.jpg",
		shortDesc: "Provide insulation while gently diffusing natural light.",
	},

	// --- Zebra Shades ---
	Zebra: {
		name: "Zebra Shades",
		link: "/shop?type=Zebra",
		image: "/images/newHome/shades/privacy_zebra.jpg",
		shortDesc:
			"Modern striped style with versatile, adjustable light and privacy control.",
	},

	// --- Sheer Shades ---
	Sheer: {
		// Generic Sheer (includes Transparent)
		name: "Sheer Shades",
		link: "/shop?type=Sheer",
		image: "/images/shadeinfo/shades/sheer/sheer.jpg",
		shortDesc: "Elegant fabrics that maximize natural light and outdoor views.",
	},
	"Light Filtering Sheer": {
		// If specifically recommended for light filtering balance
		name: "Light Filtering Sheer Shades",
		// Link might be the same as generic Sheer, or add a lightControl filter if applicable
		link: "/shop?type=Sheer&lightControl=Soft+Filter", // Assuming 'Soft Filter' applies
		image: "/images/shadeinfo/shades/sheer/sheer.jpg", // Reuse sheer image
		shortDesc:
			"Softens incoming light while offering a degree of daytime privacy.",
	},

	// --- Fallback (If a recommendation key isn't found) ---
	Default: {
		name: "Custom Shades",
		link: "/shop",
		image: "/images/logoDark-1.png", // Generic placeholder
		shortDesc: "Explore our full collection of custom window treatments.",
	},
};

// --- Main Quiz Screen Component ---
const ProductQuiz = () => {
	const [currentStep, setCurrentStep] = useState(1);
	const [answers, setAnswers] = useState({});
	const [recommendations, setRecommendations] = useState([]);
	const [quizCompleted, setQuizCompleted] = useState(false);

	// --- Filter questions based on conditions ---
	const activeQuestions = useMemo(() => {
		return quizQuestions.filter((q) => !q.condition || q.condition(answers));
	}, [answers]);

	const totalSteps = activeQuestions.length;

	const currentQuestionData = activeQuestions[currentStep - 1];

	// --- Handlers ---
	const handleAnswerSelect = useCallback(
		(questionId, value) => {
			setAnswers((prev) => ({ ...prev, [questionId]: value }));
			// Optional: Auto-advance to next step
			// if (currentStep < totalSteps) {
			//     setCurrentStep(prev => prev + 1);
			// } else {
			//     // Calculate results on final question selection
			//     const results = getRecommendations({ ...answers, [questionId]: value });
			//     setRecommendations(results);
			//     setQuizCompleted(true);
			// }
		},
		[
			/* currentStep, totalSteps, answers */
		]
	); // Dependencies removed if not auto-advancing

	const handleNext = useCallback(() => {
		if (currentStep < totalSteps) {
			setCurrentStep((prev) => prev + 1);
		} else {
			// Calculate Recommendations
			console.log("Calculating Recommendations with answers:", answers);
			const results = getRecommendations(answers);
			console.log("Recommendations:", results);
			setRecommendations(results);
			setQuizCompleted(true);
			// window.scrollTo({ top: 0, behavior: "smooth" }); // Scroll up to show results
		}
	}, [currentStep, totalSteps, answers]);

	const handleBack = useCallback(() => {
		if (currentStep > 1) {
			setCurrentStep((prev) => prev - 1);
		}
	}, [currentStep]);

	const handleRestart = useCallback(() => {
		setAnswers({});
		setCurrentStep(1);
		setRecommendations([]);
		setQuizCompleted(false);
	}, []);

	// Disable Next button if no answer selected for current step
	const isNextDisabled = !answers[currentQuestionData?.id];

	return (
		<div>
			<Helmet>
				<title>
					Find Your Perfect Shade | Product Recommendation Quiz | Off Cut Shades
				</title>
				<meta
					name="description"
					content="Answer a few simple questions and let Off Cut Shades recommend the best custom window blinds or shades for your needs based on light control, privacy, style, and more."
				/>
				<link rel="canonical" href="https://offcutshades.com/quiz" />{" "}
				{/* Example URL */}
				<meta name="robots" content="index, follow" />
			</Helmet>

			{/* --- Header / Stepper Area --- */}
			{/* <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md shadow-sm border-b border-gray-200">
				<div className="container mx-auto px-4 py-4">
					{!quizCompleted ? (
						// Pass simplified steps to Stepper
						<Stepper
							steps={activeQuestions.map((q, i) => ({
								page: i + 1,
								name: `Q${i + 1}`,
							}))} // Simple Q1, Q2...
							currentStep={currentStep}
							onStepClick={() => {}} // Disable clicking steps directly
						/>
					) : (
						<div className="text-center font-semibold text-primary">
							Quiz Complete! Your Recommendations:
						</div>
					)}
				</div>
			</div> */}

			<main className="container mx-auto px-4 max-w-3xl">
				<AnimatePresence mode="wait">
					{!quizCompleted ? (
						// --- Quiz Question ---
						<motion.div
							key={currentStep}
							initial={{ opacity: 0, x: 50 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: -50 }}
							transition={{ duration: 0.3, ease: "easeInOut" }}
						>
							<div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 md:p-8">
								<h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-2 text-center md:text-left">
									<span className="text-primary mr-1">Q{currentStep}.</span>{" "}
									{currentQuestionData?.question}
								</h2>
								{/* Optional: Subtitle or hint */}
								<p className="text-sm text-gray-500 mb-6 text-center md:text-left">
									Select the option that best fits your needs.
								</p>

								{/* Options Grid */}
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
									{currentQuestionData?.options.map((option) => {
										const isSelected =
											answers[currentQuestionData.id] === option.value;
										const IconComp = option.icon || HelpCircle; // Fallback icon
										return (
											<motion.button
												key={option.value}
												type="button"
												onClick={() =>
													handleAnswerSelect(
														currentQuestionData.id,
														option.value
													)
												}
												className={`p-4 border-2 rounded-lg text-left flex items-start gap-3 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-1
                                                    ${
																											isSelected
																												? "border-primary bg-primary/5 ring-2 ring-primary/20 shadow-md"
																												: "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
																										}
                                                `}
												whileTap={{ scale: 0.97 }}
											>
												<div
													className={`mt-1 flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${
														isSelected
															? "bg-primary/10 text-primary"
															: "bg-gray-100 text-gray-500"
													}`}
												>
													<IconComp size={20} strokeWidth={2} />
												</div>
												<div>
													<span className="font-medium text-base text-gray-800">
														{option.text}
													</span>
													{option.description && (
														<p className="text-xs text-gray-500 mt-1">
															{option.description}
														</p>
													)}
												</div>
											</motion.button>
										);
									})}
								</div>

								{/* Navigation Buttons */}
								<div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-100">
									<button
										onClick={handleBack}
										disabled={currentStep === 1}
										className="btn btn-secondary-outline text-sm py-2 disabled:opacity-40 disabled:cursor-not-allowed"
									>
										<ArrowLeft size={16} /> Back
									</button>
									<button
										onClick={handleNext}
										disabled={isNextDisabled}
										className="border-primary btn btn-primary text-sm p-2"
									>
										{currentStep === totalSteps
											? "Show My Recommendations"
											: "Next Question"}
										<ArrowRight size={16} />
									</button>
								</div>
							</div>
						</motion.div>
					) : (
						// --- Quiz Results ---
						<motion.div
							key="results"
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.4, ease: "easeOut" }}
						>
							<div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 md:p-8 text-center">
								<motion.div
									initial={{ scale: 0.8, opacity: 0 }}
									animate={{ scale: 1, opacity: 1 }}
									transition={{ delay: 0.1, type: "spring" }}
									className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-4"
								>
									<Check size={36} strokeWidth={2} />
								</motion.div>
								<h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-4">
									Your Shade Recommendations
								</h2>
								<p className="text-gray-600 mb-8 max-w-lg mx-auto">
									Based on your answers, here are the top shade types we think
									you'll love:
								</p>

								{/* Recommendations Grid */}
								<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
									{recommendations.map((rec, index) => (
										<motion.div
											key={rec.name}
											initial={{ opacity: 0, y: 20 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.2 + index * 0.1 }}
											className="border border-gray-200 rounded-lg h-full"
										>
											<img
												src={rec.image}
												alt={rec.name}
												className="aspect-video rounded object-cover border border-gray-100"
											/>
											<div className="gap-2 p-5 text-left flex flex-col">
												<h3 className="font-semibold text-lg text-gray-800 mb-1">
													{rec.name}
												</h3>
												<p className="text-sm text-gray-600 mb-3 flex-grow">
													{rec.shortDesc}
												</p>
												{rec.reasons && rec.reasons.length > 0 && (
													<div className="mb-3">
														<p className="text-xs font-semibold text-primary mb-1">
															Why we recommend it:
														</p>
														<ul className="list-disc list-outside pl-4 space-y-0.5 text-xs text-gray-500">
															{rec.reasons.map((reason, rIndex) => (
																<li key={rIndex}>{reason}</li>
															))}
														</ul>
													</div>
												)}
												<Link
													to={rec.link}
													className="mt-auto block w-full btn btn-primary-outline text-xs py-1.5"
												>
													View {rec.name}
												</Link>
											</div>
										</motion.div>
									))}
								</div>

								{/* Further Actions */}
								<div className="pt-6 border-t border-gray-100 space-y-4">
									<p className="text-sm text-gray-600">
										See the fabrics for yourself!
									</p>
									<div className="flex flex-col sm:flex-row justify-center gap-3">
										<Link to="/swatches" className="btn btn-secondary text-sm">
											<SwatchBook size={16} /> Order Free Samples
										</Link>
										<button
											onClick={handleRestart}
											className="btn btn-secondary-outline text-sm"
										>
											<RotateCcw size={16} /> Retake Quiz
										</button>
									</div>
									<p className="text-xs text-gray-500 mt-2">
										Or,{" "}
										<Link to="/shop" className="text-primary hover:underline">
											browse all shades
										</Link>
										.
									</p>
								</div>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</main>
		</div>
	);
};

export default ProductQuiz;
